import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, PermissionItem, useAddTWSessionKey, useB3Config, useGetAllTWSigners } from "../../../../global-account/react/index.js";
import { debugB3React } from "../../../../shared/utils/debug.js";
import { formatAddress } from "../../../../shared/utils/formatAddress.js";
import { CreditCard, Eye } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import TimeAgo from "react-timeago";
import { useActiveAccount } from "thirdweb/react";
const debug = debugB3React("RequestPermissions");
/**
 * Component for requesting permissions to access user accounts and contracts
 * Allows users to approve session keys with specific permission settings
 */
export function RequestPermissions({
  onSuccess,
  onError,
  chain,
  sessionKeyAddress,
  permissions
}) {
  const [isApproving, setIsApproving] = useState(false);
  const account = useActiveAccount();
  const {
    defaultPermissions
  } = useB3Config();
  const DEFAULT_PERMISSIONS = useMemo(() => permissions ?? defaultPermissions, [defaultPermissions, permissions]);
  const {
    refetch: refetchSigners
  } = useGetAllTWSigners({
    chain,
    accountAddress: account?.address
  });
  const {
    newSessionKey
  } = useAddTWSessionKey({
    onSuccess: transactionResult => {
      console.log("@@transactionResult:", transactionResult);
      onSuccess?.();
    },
    onError: error => {
      console.error("@@error:", error);
      onError?.(error);
    },
    refetchSigners: () => {
      return refetchSigners();
    },
    chain
  });
  const handleApprove = useCallback(async () => {
    debug("@@handleApprove");
    try {
      setIsApproving(true);
      if (!sessionKeyAddress) {
        throw new Error("Session key address is required");
      }
      console.log("@@sessionKeyAddress:", sessionKeyAddress);
      await newSessionKey({
        sessionKeyAddress: sessionKeyAddress,
        approvedTargets: DEFAULT_PERMISSIONS.approvedTargets,
        nativeTokenLimitPerTransaction: DEFAULT_PERMISSIONS.nativeTokenLimitPerTransaction,
        permissionStartTimestamp: DEFAULT_PERMISSIONS.startDate,
        permissionEndTimestamp: DEFAULT_PERMISSIONS.endDate
      });
    } catch (error_0) {
      console.error("@@error:", error_0);
      onError?.(error_0);
    } finally {
      setIsApproving(false);
    }
  }, [DEFAULT_PERMISSIONS, newSessionKey, onError, sessionKeyAddress]);
  const formatDate = date => {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short"
    }).format(date);
  };
  return _jsxs("div", {
    className: "flex flex-col gap-4",
    children: [_jsxs("div", {
      className: "text-center",
      children: [_jsx("h2", {
        className: "mb-2 text-2xl font-bold",
        children: "Allow Access"
      }), _jsx("p", {
        className: "text-b3-react-muted-foreground opacity-70",
        children: "This app would like permission to:"
      })]
    }), _jsx(PermissionItem, {
      title: "Make transactions on your behalf",
      description: _jsxs("span", {
        children: ["Up to", " ", _jsxs("span", {
          className: "text-b3-react-primary font-medium",
          children: [DEFAULT_PERMISSIONS.nativeTokenLimitPerTransaction, " ETH"]
        }), " ", "per transaction"]
      }),
      icon: _jsx(CreditCard, {
        className: "h-5 w-5"
      })
    }), _jsx(PermissionItem, {
      title: "View your account",
      description: "See your wallet address and balance",
      icon: _jsx(Eye, {
        className: "h-5 w-5"
      })
    }), _jsxs("div", {
      className: "bg-b3-react-card rounded-lg border p-5 py-4",
      children: [_jsx("h3", {
        className: "mb-2 text-base font-medium",
        children: "Approved Contracts"
      }), _jsx("div", {
        className: "space-y-2",
        children: DEFAULT_PERMISSIONS.approvedTargets.map(target => _jsxs("div", {
          className: "flex items-center gap-2",
          children: [_jsx("div", {
            className: "bg-b3-react-background text-b3-react-muted-foreground rounded-md border px-3 py-2 font-mono text-sm",
            children: formatAddress(target)
          }), _jsx("span", {
            className: "text-b3-react-muted-foreground text-sm",
            children: "\u2022 Game Contract"
          })]
        }, target))
      })]
    }), _jsxs("div", {
      className: "bg-b3-react-card rounded-lg border p-5 py-4",
      children: [_jsx("h3", {
        className: "mb-2 text-base font-medium",
        children: "Permission"
      }), _jsxs("div", {
        className: "space-y-1 text-sm",
        children: [_jsxs("div", {
          className: "text-b3-react-muted-foreground",
          children: [_jsx("span", {
            children: `Valid for `
          }), _jsx("span", {
            children: _jsx(TimeAgo, {
              date: DEFAULT_PERMISSIONS.endDate,
              live: false,
              formatter: (value, unit) => {
                if (unit === "year") {
                  return "1 year";
                }
                return `${value} ${unit}${value !== 1 ? "s" : ""}`;
              }
            })
          }), _jsx("span", {
            className: "text-b3-react-muted-foreground ml-1 opacity-70",
            children: ` (until ${formatDate(DEFAULT_PERMISSIONS.endDate)})`
          })]
        }), DEFAULT_PERMISSIONS.startDate.getTime() > Date.now() + 5 * 60 * 1000 && _jsxs("div", {
          className: "text-b3-react-muted-foreground",
          children: [_jsx("span", {
            children: `Starting: `
          }), _jsx("span", {
            className: "ml-1",
            children: formatDate(DEFAULT_PERMISSIONS.startDate)
          })]
        })]
      })]
    }), _jsxs("div", {
      className: "space-y-3 pt-4",
      children: [_jsx(Button, {
        onClick: handleApprove,
        disabled: isApproving,
        className: "w-full py-6 text-lg font-medium",
        children: isApproving ? "Approving..." : "Allow Access"
      }), _jsx("p", {
        className: "text-b3-react-muted-foreground text-center text-sm",
        children: "Revoke access anytime in settings"
      })]
    })]
  });
}